import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetailOverviewDestinations from 'components/composites/DetailOverviewDestinations';
import MobileDetailScreen from 'components/composites/MobileDetailScreen';
import { DetailOverviewBody, DetailOverviewCard, DetailOverviewStack } from 'components/ui/DetailOverview';
import { readWorkspaceDataset, writeWorkspaceDataset } from 'workspaceRecords';
function filterProjects(projects, filter, query) {
    return projects.filter((project) => {
        const matchesFilter = filter === 'all' || project.agentIds.length > 0;
        const matchesQuery = !query.trim() || project.name.toLowerCase().includes(query.trim().toLowerCase());
        return matchesFilter && matchesQuery;
    });
}
export default function LeadsWorkspace({ links, embedded = false }) {
    const { t } = useTranslation();
    const [dataset, setDataset] = useState(() => readWorkspaceDataset());
    const [selectedLeadId] = useState('brookfield-capital');
    const [activeTab, setActiveTab] = useState('activity');
    const [assignedFilter, setAssignedFilter] = useState('active');
    const [availableFilter, setAvailableFilter] = useState('active');
    const [assignedQuery, setAssignedQuery] = useState('');
    const [availableQuery, setAvailableQuery] = useState('');
    const [assignedDraftQuery, setAssignedDraftQuery] = useState('');
    const [availableDraftQuery, setAvailableDraftQuery] = useState('');
    useEffect(() => {
        writeWorkspaceDataset(dataset);
    }, [dataset]);
    const selectedLead = dataset.leads.find((lead) => lead.id === selectedLeadId) || dataset.leads[0] || null;
    const assignedProjectRecords = useMemo(() => (selectedLead ? dataset.projects.filter((project) => selectedLead.projectIds.includes(project.id)) : []), [dataset.projects, selectedLead]);
    const availableProjectRecords = useMemo(() => (selectedLead ? dataset.projects.filter((project) => !selectedLead.projectIds.includes(project.id)) : []), [dataset.projects, selectedLead]);
    const assignedProjects = useMemo(() => filterProjects(assignedProjectRecords, assignedFilter, assignedQuery), [assignedFilter, assignedProjectRecords, assignedQuery]);
    const availableProjects = useMemo(() => filterProjects(availableProjectRecords, availableFilter, availableQuery), [availableFilter, availableProjectRecords, availableQuery]);
    const onAssign = (projectId) => {
        if (!selectedLead) {
            return;
        }
        setDataset((current) => ({
            ...current,
            leads: current.leads.map((lead) => lead.id === selectedLead.id && !lead.projectIds.includes(projectId)
                ? {
                    ...lead,
                    projectIds: [...lead.projectIds, projectId],
                }
                : lead),
        }));
    };
    const onUnassign = (projectId) => {
        if (!selectedLead) {
            return;
        }
        setDataset((current) => ({
            ...current,
            leads: current.leads.map((lead) => lead.id === selectedLead.id
                ? {
                    ...lead,
                    projectIds: lead.projectIds.filter((item) => item !== projectId),
                }
                : lead),
        }));
    };
    if (!selectedLead) {
        return null;
    }
    return (_jsx(MobileDetailScreen, { activityLabel: t('lead_tab_activity'), activeTab: activeTab, filterOptions: [
            { value: 'active', label: t('lead_filter_active') },
            { value: 'all', label: t('lead_filter_all') },
        ], firstActionLabel: t('lead_unassign'), firstActionTone: "danger", firstChipLabel: t('lead_chip_assigned'), firstFilterValue: assignedFilter, firstItems: assignedProjects.map((project) => ({
            id: project.id,
            name: project.name,
            meta: project.id === 'tower-a' ? 'Activity' : 'Overview',
        })), firstPanelTitle: t('lead_assigned_projects', { count: assignedProjectRecords.length }), firstSearchPlaceholder: t('lead_search_placeholder'), firstSearchValue: assignedDraftQuery, firstTotalCount: assignedProjectRecords.length, onDelete: () => undefined, onFirstAction: onUnassign, onFirstFilterChange: (value) => setAssignedFilter(value), onFirstSearchChange: setAssignedDraftQuery, onFirstSearchSubmit: () => setAssignedQuery(assignedDraftQuery.trim()), onSecondAction: onAssign, onSecondFilterChange: (value) => setAvailableFilter(value), onSecondSearchChange: setAvailableDraftQuery, onSecondSearchSubmit: () => setAvailableQuery(availableDraftQuery.trim()), onTabChange: setActiveTab, overviewContent: _jsxs(DetailOverviewStack, { children: [_jsx(DetailOverviewCard, { title: t('lead_overview_heading'), children: _jsx(DetailOverviewBody, { children: `${selectedLead.company} · ${selectedLead.summary}` }) }), _jsx(DetailOverviewCard, { title: t('lead_destinations_heading'), children: _jsx(DetailOverviewDestinations, { links: links }) })] }), overviewLabel: t('lead_tab_overview'), secondActionLabel: t('lead_assign'), secondActionTone: "primary", secondChipLabel: t('lead_chip_available'), secondFilterValue: availableFilter, secondItems: availableProjects.map((project) => ({
            id: project.id,
            name: project.name,
            meta: project.id === 'tower-a' ? 'Activity' : 'Overview',
        })), secondPanelTitle: t('lead_available_projects', { count: availableProjectRecords.length }), secondSearchPlaceholder: t('lead_search_placeholder'), secondSearchValue: availableDraftQuery, secondTotalCount: availableProjectRecords.length, showAppBar: !embedded, title: selectedLead.name }));
}
